<template>
	<BaseTableRow
		:columns="columns"
		:indent-space="indentSpace"
		:sticky-columns="stickyColumns"
		:with-padding="withPadding"
		:clean="clean"
		:show-actions="!clean"
		:actions="[]"
	>
		<template #checkbox>
			<Rectangle
				height="18px"
				width="18px"
				:custom="{ borderRadius: '2px', flexShrink: 0 }"
			/>
		</template>
		<BaseTableCol
			v-if="leadingSpan"
			slot="header"
			:span="leadingSpan"
			:width-preset="leadingSpan < 0.2 ? 'spacer' : 'compact'"
		/>
		<template v-if="columns && columns.length">
			<BaseTableCol
				v-for="(column, index) in columns"
				:key="column.sortKey || index"
				v-bind="{ span: column.span, column }"
			>
				<Rectangle
					v-if="isImageColumn(column, index) && !fullWidth"
					:height="size === 'large' ? '80px' : '50px'"
					:width="size === 'large' ? '110px' : '70px'"
				/>
				<div
					v-else-if="isImageColumn(column, index) && fullWidth"
					:class="$style['skeleton-media-col']"
				>
					<Rectangle
						:height="size === 'large' ? '80px' : '50px'"
						:width="size === 'large' ? '110px' : '70px'"
					/>
					<div :class="$style['skeleton-media-content']">
						<Rectangle height="23px" width="100%" />
						<Rectangle
							v-if="size === 'large'"
							height="15px"
							width="55%"
							:custom="{ marginTop: '5px' }"
						/>
					</div>
				</div>
				<div
					v-else
					:class="$style['skeleton-col-content']"
				>
					<Rectangle height="23px" :width="lineWidth(index)" />
					<Rectangle
						v-if="index === 0 && size === 'large'"
						height="15px"
						width="30%"
						:custom="{ marginTop: '5px' }"
					/>
				</div>
			</BaseTableCol>
		</template>
		<template v-else>
			<div
				v-for="n in fallbackColumns"
				:key="n"
				:class="$style['skeleton-col']"
				:style="{ flex: 1 }"
			>
				<Rectangle height="23px" :width="lineWidth(n - 1)" />
			</div>
		</template>
		<BaseTableCol
			v-if="trailingSpan"
			:span="trailingSpan"
			:width-preset="trailingSpan < 1 ? 'compact' : 'text'"
		/>
	</BaseTableRow>
</template>

<script>
import Rectangle from './Rectangle'

const WIDTHS = ['80%', '70%', '60%', '50%', '65%', '55%', '75%', '45%']
const IMAGE_SORT_KEYS = ['parts', 'category', 'name']

export default {
	name: 'TableRowSkeleton',
	components: {
		Rectangle
	},
	props: {
		columns: {
			type: Array,
			default: () => []
		},
		size: {
			type: String,
			default: 'default'
		},
		hasImage: {
			type: Boolean,
			default: false
		},
		fallbackColumns: {
			type: Number,
			default: 5
		},
		leadingSpan: {
			type: Number,
			default: 0
		},
		trailingSpan: {
			type: Number,
			default: 0
		},
		indentSpace: {
			type: Boolean,
			default: false
		},
		stickyColumns: {
			type: Number,
			default: 1
		},
		withPadding: {
			type: [Boolean, String],
			default: true
		},
		clean: {
			type: [Boolean, String],
			default: false
		},
		fullWidth: {
			type: Boolean,
			default: true
		},
		imageColumnKey: {
			type: [String, Array],
			default: null
		}
	},
	computed: {
		resolvedImageColumnIndex () {
			if (!Array.isArray(this.columns) || !this.columns.length) {
				return 0
			}

			const keys = Array.isArray(this.imageColumnKey)
				? this.imageColumnKey
				: this.imageColumnKey
					? [this.imageColumnKey]
					: []

			if (keys.length) {
				const byExplicitKey = this.columns.findIndex(column => keys.includes(column && column.sortKey))
				if (byExplicitKey >= 0) return byExplicitKey
			}

			const byKnownKey = this.columns.findIndex(column => IMAGE_SORT_KEYS.includes(column && column.sortKey))
			if (byKnownKey >= 0) return byKnownKey

			const byChildren = this.columns.findIndex(column => Array.isArray(column && column.children) && column.children.length)
			if (byChildren >= 0) return byChildren

			return 0
		}
	},
	methods: {
		isImageColumn (column, index) {
			if (!this.hasImage) return false
			return index === this.resolvedImageColumnIndex
		},
		lineWidth (index) {
			return this.fullWidth ? '100%' : this.rectangleWidth(index)
		},
		rectangleWidth (index) {
			return WIDTHS[index % WIDTHS.length]
		}
	}
}
</script>

<style lang="scss" module>
.skeleton-col {
	display: flex;
	flex-flow: column;
	justify-content: center;
	padding: 20px 15px;
	min-width: 0;
}

.skeleton-col-content {
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;
	min-width: 0;
}

.skeleton-media-col {
	display: flex;
	align-items: center;
	gap: 15px;
	width: 100%;
	min-width: 0;
}

.skeleton-media-content {
	display: flex;
	flex-direction: column;
	justify-content: center;
	min-width: 0;
	width: 100%;
}
</style>
